import React, { PureComponent } from "react";
import { connect } from "react-redux";
import debounce from "lodash.debounce";

import {
  AddBox,
  Delete,
  FileCopy,
  CloudUpload,
  Sync
} from "@material-ui/icons";
import { Grid, IconButton, Tooltip, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import DesignTargetApi from "MetaComponent/api/DesignTarget";
import DirectoryExplorerApi from "MetaComponent/api/DirectoryExplorer";
import Script2DApi from "MetaComponent/api/Script2D";
import SelectedDesignTargetApi from "MetaComponent/api/SelectedDesignTarget";

import HelperUtils from "MetaCell/helper/HelperUtils";
import Helper from "MetaComponent/helper/DesignTarget";

import DirectoryExplorerSelector from "MetaComponent/selectors/DirectoryExplorer";
import DesignTargetSelector from "MetaComponent/selectors/DesignTarget";
import { FamilySelector } from "MetaCell/selectors/Family";
import FFWTTargetSelector from "MetaComponent/selectors/FFWTTarget";
import SelectedDesignTargetSelector from "MetaComponent/selectors/SelectedDesignTarget";
import Script2DSelector from "MetaComponent/selectors/Script2D";

import ConfirmDialogAction from "BaseApp/actions/ConfirmDialog";

import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import EditActions from "components/EditActions/EditActions";
import IconTooltip from "components/IconTooltip/IconTooltip";
import TargetsView from "./components/TargetsView/TargetsView";
import TargetsSelection from "./components/TargetsSelection/TargetsSelection";
import DirectionSnackbar from "components/Snackbar/Snackbar";
import Import from "components/Import";
import JobStatusAndProgress from "components/JobStatusAndProgress/JobStatusAndProgress";

const styles = theme => ({
  root: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    margin: 30
  },
  title: {
    float: "left"
  },
  tooltip: {
    float: "left"
  }
});

/**
 * A class component to connect to redux state and serve the form. See {@link MetaCellGlobalParametersForm}
 * @typedef {Component} MetaCellGlobalParameters
 */
export class ComponentTargets extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      editingSelectedTargets: null,
      warnUserAboutFamilyMembers: false,
      importDialogOpen: false,
      importProcessing: false,
      designTargetUpdateProgress: false,
      snackbar: {
        message: "",
        visible: false
      }
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * it checks whether the given design targets' dimensions are compatible.
   * it also converts to a comparable unit before the comparison.
   * @param {Number[]} dtIds - the design target ids
   * @param {Number[]} beingDeselectedTargetIds - unselected design target ids
   * @param {Number[]} beingNewlyAddedTargetIds - newly added design targert ids
   * @param {Number} width - the compatible width
   * @param {Number} height - the compatible height
   * @param {String} unit - the compatible unit
   */
  getIncompatibleDesignTargets(
    dtIds,
    beingDeselectedTargetIds,
    beingNewlyAddedTargetIds,
    width,
    height,
    unit
  ) {
    const { designTargets } = this.props,
      designTargetObjects = dtIds
        .filter(id => beingNewlyAddedTargetIds.includes(id))
        .map(id => {
          if (beingDeselectedTargetIds.includes(id)) return;
          return designTargets.byId[id];
        });
    return Helper.getIncompatibleDesignTargets(
      designTargetObjects,
      width,
      height,
      unit
    );
  }

  /**
   * it updates the editing selected design targets with new ones but if incompatible
   * dimensions are detected it opens a dialog to duplicate the incompatible ones with compatible
   * dimensions
   * @param {Number[]} newSelectedTargets - an array of design target Id's
   * @callback
   */
  updateSelectedTargets = newSelectedTargets => {
    const { editingSelectedTargets } = this.state;
    const beingDeselectedTargetIds = editingSelectedTargets.filter(
      id => !newSelectedTargets.includes(id)
    );
    const beingNewlyAddedTargetIds = newSelectedTargets.filter(
      id => !editingSelectedTargets.includes(id)
    );
    const { showConfirmDialog } = this.props,
      openMetaComponent = this.getOpenMetaComponent(),
      { width, height, unit } = openMetaComponent,
      incompatibleDesignTargets = this.getIncompatibleDesignTargets(
        newSelectedTargets,
        beingDeselectedTargetIds,
        beingNewlyAddedTargetIds,
        width,
        height,
        unit
      );
    if (
      beingDeselectedTargetIds.length > 0 ||
      incompatibleDesignTargets.length === 0
    ) {
      this.setState({
        editingSelectedTargets: newSelectedTargets
      });
    } else {
      const incompatibleDTNames = incompatibleDesignTargets.map(
        incompatibleDT => incompatibleDT.name
      );
      showConfirmDialog(
        "Incompatible Dimensions",
        `The near field dimensions of the design targets below are not compatible with the meta component dimensions:\n\n    ${incompatibleDTNames.join(
          "\n    "
        )}\n\nHow would you like to handle this?`,
        () => {
          this.setState({
            editingSelectedTargets: newSelectedTargets
          });
        },
        () => {
          let icdt_ids = incompatibleDesignTargets.map(
            incompatibleDT => incompatibleDT.id
          );
          this.setState({
            editingSelectedTargets: newSelectedTargets.filter(
              nst => !icdt_ids.includes(nst)
            )
          });
        },
        false,
        () => {},
        false,
        false,
        ["Confirm Anyway", "Cancel"]
      );
    }
  };

  /**
   * it changes the editing state with the open meta component selected design targets
   */
  editSelectedTargets = () => {
    const openMetaComponent = this.getOpenMetaComponent(),
      { selected_design_targets } = openMetaComponent;
    if (
      openMetaComponent.selectedFM.length === 0 &&
      !Boolean(openMetaComponent.ideal_family)
    ) {
      this.showNoFamilyMemberWarning();
      return;
    }
    this.setState({
      editingSelectedTargets: selected_design_targets
        ? selected_design_targets.map(
            sdtId => this.props.selectedDesignTargets.byId[sdtId].design_target
          )
        : []
    });
  };

  /**
   * it changes the editing state to null
   */
  cancelEditingSelectedTargets = () => {
    this.setState({
      editingSelectedTargets: null
    });
  };

  /**
   * it saves the selected design targets and leaves the editing mode,
   * in case the currently focused design target is no longer selected,
   * it removes the focus.
   */
  save = async () => {
    const {
        openMetaComponentId,
        focusedDesignTargetId,
        selectedDesignTargets,
        createAndDeleteSelectedDesignTargets
      } = this.props,
      { editingSelectedTargets } = this.state,
      focusedDesignTargetIsNoLongerSelected =
        focusedDesignTargetId &&
        !editingSelectedTargets.includes(focusedDesignTargetId),
      selectedDesignTargetObjs = Object.values(selectedDesignTargets.byId),
      designTargetsCurrentlySelectedIds = selectedDesignTargetObjs.map(
        sdt => sdt.design_target
      ),
      designTargetsToSelect = editingSelectedTargets.filter(
        id => !designTargetsCurrentlySelectedIds.includes(id)
      ),
      designTargetsToUnselect = designTargetsCurrentlySelectedIds.filter(
        id => !editingSelectedTargets.includes(id)
      ),
      selectedDesignTargetsToUnselect = selectedDesignTargetObjs.filter(sdt =>
        designTargetsToUnselect.includes(sdt.design_target)
      ),
      sdtToUnselectIds = selectedDesignTargetsToUnselect.map(sdt => sdt.id);

    createAndDeleteSelectedDesignTargets(
      designTargetsToSelect,
      openMetaComponentId,
      sdtToUnselectIds
    );

    if (focusedDesignTargetIsNoLongerSelected) {
      this.props.focusOnDesignTarget(0);
    }
    this.cancelEditingSelectedTargets();
  };

  /**
   * @returns {Object} the open meta component entity
   */
  getOpenMetaComponent = () => {
    const { openMetaComponentId, metaComponents } = this.props;
    return metaComponents.byId[openMetaComponentId];
  };

  /**
   * it gets the ids of the meta component's selected design targets and builds an array
   * of design target objects from it
   * @returns {Object[]} an array of component design targets
   */
  getComponentTargets = selectedDesignTargetsIds => {
    const { designTargets, selectedDesignTargets, ffwfTargets } = this.props;
    const allTargetsAreValid =
      selectedDesignTargetsIds.length &&
      selectedDesignTargetsIds.every(id => selectedDesignTargets.byId[id]);
    if (!allTargetsAreValid) {
      return [];
    }
    const targets = selectedDesignTargetsIds.map(selectedTargetId => {
      const selectedDesignTarget = selectedDesignTargets.byId[selectedTargetId];
      const targetId = selectedDesignTarget.design_target;
      const designTarget = designTargets.byId[targetId];
      const ffId = designTarget && designTarget.FFWFTarget;
      return Object.assign({}, designTargets.byId[targetId], {
        farfield_target: ffId && ffwfTargets.byId[ffId].name,
        nearfield_dimensions: `${(designTarget && designTarget.NFWidth) ||
          ""} ${designTarget && designTarget.unit} x ${(designTarget &&
          designTarget.NFHeight) ||
          ""} ${designTarget && designTarget.unit}`
      });
    });
    return targets;
  };

  /**
   * it creates a design target and immediately associates it with the component
   * selected design targets
   * @callback
   */
  createComponentDesignTarget = async () => {
    const openMetaComponent = this.getOpenMetaComponent();
    if (
      openMetaComponent.selectedFM.length === 0 &&
      !Boolean(openMetaComponent.ideal_family)
    ) {
      this.showNoFamilyMemberWarning();
      return;
    }
    const { createSelectedDesignTarget, focusOnDesignTarget } = this.props,
      newDesignTargetId = await this.adaptAndCreateDesignTarget({
        name: "new design target"
      });
    await createSelectedDesignTarget(newDesignTargetId, openMetaComponent.id);
    focusOnDesignTarget(newDesignTargetId);
  };

  getDesignTargetUsedMCs = async toBeDeletedDesignTargetId => {
    return await DesignTargetApi.getDesignTargetUsedMCs(
      toBeDeletedDesignTargetId
    );
  };

  /**
   * @param {Object} metaComponent
   * @returns {Object} the family that is linked to the component
   */
  getComponentFamily(metaComponent) {
    if (metaComponent.ideal_family) {
      return metaComponent.ideal_family;
    }
    const { families } = this.props;
    return { ...families.byId[metaComponent.family] };
  }

  deleteComponentDesignTarget = async () => {
    const {
      deleteDesignTargetAction,
      selectedDesignTargets,
      showConfirmDialog
    } = this.props;
    const openMetaComponent = this.getOpenMetaComponent();
    let toBeDeletedDesignTargetId = this.props.focusedDesignTargetId;
    var usedMCs = await this.getDesignTargetUsedMCs(toBeDeletedDesignTargetId);
    usedMCs = usedMCs?.filter(
      usedMcName => usedMcName !== openMetaComponent.name
    );
    showConfirmDialog(
      "Are you sure you want to delete this design target?",
      usedMCs?.length > 0
        ? [
            `This design target is also being used by ${HelperUtils.joinWords(
              usedMCs
            )} Please select one of the following options`
          ]
        : [`Please select one of the following options`],
      [
        [
          {
            option:
              usedMCs?.length > 0
                ? "Delete for all (permanently)"
                : "Delete permanently",
            actions: [
              // delete call
              (resolve, reject) => {
                deleteDesignTargetAction(toBeDeletedDesignTargetId).then(() => {
                  this.cancelEditingSelectedTargets();
                  this.props.focusOnDesignTarget(0);
                });
                resolve();
              }
            ]
          },
          {
            option:
              usedMCs?.length > 0
                ? "Delete for this target (but keep in library)"
                : "Delete but keep in library",
            actions: [
              // delete call
              async (resolve, reject) => {
                const openMcSelectedTargets = Object.values(
                  selectedDesignTargets?.byId
                ).map(({ design_target }) => design_target);
                const newlySelectedTargets = openMcSelectedTargets.filter(
                  id => id !== toBeDeletedDesignTargetId
                );
                this.setState({
                  editingSelectedTargets: openMcSelectedTargets
                });
                this.updateSelectedTargets(newlySelectedTargets);
                await this.save();
                resolve();
              }
            ]
          }
        ]
      ],
      () => {},
      false,
      () => {},
      false,
      false,
      ["Delete", "Cancel"]
    );
  };

  async recreateDesignTargets(deletePermanently = false) {
    const {
      deleteDesignTargetAction,
      designTargets,
      selectedDesignTargets
    } = this.props;
    const openMetaComponent = this.getOpenMetaComponent(),
      selectedDesignTargetsObjs = Object.values(selectedDesignTargets?.byId),
      designTargetCount = selectedDesignTargets.allIds.length;
    this.cancelEditingSelectedTargets();
    this.props.focusOnDesignTarget(0);

    if (
      openMetaComponent.selectedFM.length === 0 &&
      !Boolean(openMetaComponent.ideal_family)
    ) {
      this.showNoFamilyMemberWarning();
      return;
    }

    this.setState({ designTargetUpdateProgress: 1 });
    let count = 1;
    let newlySelectedTargets = [];
    for (const sdt of selectedDesignTargetsObjs) {
      const dtId = sdt.design_target;
      const designTarget = designTargets.byId[dtId];
      if (!designTarget) continue;

      const progress = Math.round((count / designTargetCount) * 100);
      const { width, height, unit, nr_x, nr_y } = openMetaComponent;
      const componentDimensions = { width, height, unit, nr_x, nr_y };
      const messages = await Helper.getDiscrepantDesignTargetNFWFsMessages(
        [designTarget],
        this.getComponentFamily(openMetaComponent),
        componentDimensions
      );
      const thereAreDiscrepanciesToHandle = messages.some(
        message => message !== ""
      );
      if (
        !thereAreDiscrepanciesToHandle ||
        thereAreDiscrepanciesToHandle.length === 0
      ) {
        count += 1;
        newlySelectedTargets.push(dtId);
        this.setState(
          {
            snackbar: {
              message: "",
              visible: false
            }
          },
          () => {
            this.setState({
              snackbar: {
                message: `Skipping design targets that already have matching dimensions.`,
                visible: true
              }
            });
          }
        );
        continue;
      }

      // skip all design targets that do not have a nfwf design script
      if (
        !designTarget?.wave_front_design_script ||
        !this.props.script2Ds.byId[designTarget?.wave_front_design_script]
          ?.script
      ) {
        this.setState({ designTargetUpdateProgress: progress });
        count += 1;
        newlySelectedTargets.push(dtId);
        this.setState(
          {
            snackbar: {
              message: "",
              visible: false
            }
          },
          () => {
            this.setState({
              snackbar: {
                message: `There is at least one design target without a wavefront script, please replace this manually.`,
                visible: true
              }
            });
          }
        );
        continue;
      }

      try {
        // do not call anything if the user closed the component (will get a broken pipe anyway)
        if (!this._isMounted) return;

        const newDesignTargetId = await this.props.duplicateDesignTarget(dtId);
        await this.regenerateWavefrontImages(newDesignTargetId);
        await this.props.createSelectedDesignTarget(
          newDesignTargetId,
          openMetaComponent.id
        );
        if (deletePermanently) {
          await deleteDesignTargetAction(dtId);
        }
        this.setState({ designTargetUpdateProgress: progress });
        count += 1;
        newlySelectedTargets.push(newDesignTargetId);
      } catch (e) {
        this.setState({
          snackbar: {
            message: `Failed to regenerate images for ${sdt.name}`,
            visible: true
          }
        });
      }
    }
    if (!deletePermanently) {
      this.setState({
        editingSelectedTargets: newlySelectedTargets
      });
      this.updateSelectedTargets(newlySelectedTargets);
      await this.save();
    }
    this.setState({ designTargetUpdateProgress: false });
  }

  recreateComponentDesignTargets = async () => {
    const { showConfirmDialog } = this.props;
    showConfirmDialog(
      "Recreate all design targets.",
      [
        "The existing design targets will be removed and new targets will be created with the current component and metacell dimensions. \n\
Only design targets that contain a script can be recreated. The rest will be skipped.\n\n\
Please do not close this page until all design targets have been recreated.\n\n\
Please select how to handle the existing design targets.\n\n"
      ],
      [
        [
          {
            option:
              "Delete permanently - also deletes them for any meta component that currently uses them",
            actions: [
              async (resolve, reject) => {
                try {
                  this.recreateDesignTargets(true);
                } catch (e) {
                  this.setState({ snackbar: { visible: true, message: e } });
                  reject();
                }
                resolve();
              }
            ]
          },
          {
            option: "Delete but keep in library",
            actions: [
              async (resolve, reject) => {
                try {
                  this.recreateDesignTargets();
                } catch (e) {
                  this.setState({ snackbar: { visible: true, message: e } });
                  reject();
                }
                resolve();
              }
            ]
          }
        ]
      ],
      () => {},
      false,
      () => {},
      false,
      false,
      ["Recreate", "Cancel"]
    );
  };

  /**
   * Triggers async wavefront image generation for a design target.
   * @param designTargetId - design target ID to update
   */
  regenerateWavefrontImages = async designTargetId => {
    const {
      width,
      height,
      unit,
      nr_x,
      nr_y,
      family
    } = this.getOpenMetaComponent();
    const { families } = this.props;
    const familyObj = families.byId[family];
    const cellWidth = familyObj?.cell_width;
    const cellHeight = familyObj?.cell_height;
    const cellUnit = familyObj?.unit;
    const designTarget = this.props.designTargets.byId[designTargetId];

    let dtUpdateData = {
      NFWidth: width,
      NFHeight: height,
      name: designTarget?.name.replace(" (Copy)", "")
    };

    if (designTarget.wave_front_design_script) {
      dtUpdateData = {
        ...dtUpdateData,
        NFWaveFront_field: null,
        nfwf_amplitude_image_field: null,
        nfwf_phase_image_field: null
      };
    }
    try {
      await this.props.updateDesignTargetAction(designTargetId, dtUpdateData);
    } catch (e) {
      const errorMessage = e?.response?.data[0];
      this.setState({
        snackbar: {
          message: errorMessage,
          visible: true
        }
      });
      return;
    }

    const script = this.props.script2Ds.byId[
      designTarget.wave_front_design_script
    ];
    if (!script || !script.script) {
      return;
    }
    // Compose the data as in WavefrontGenerator/NFWFTargetForm
    const data = {
      code: script.script,
      parameters: script.parameters,
      dimensions: {
        componentWidth: parseFloat(width),
        componentHeight: parseFloat(height),
        cellWidth: Helper.getComponentDimensionfromMetacellDim(
          cellWidth,
          unit,
          cellUnit
        ),
        cellHeight: Helper.getComponentDimensionfromMetacellDim(
          cellHeight,
          unit,
          cellUnit
        ),
        nrX: parseFloat(nr_x),
        nrY: parseFloat(nr_y)
      }
    };
    await DesignTargetApi.startAsyncNfwfJob(designTarget.id, data);
  };

  /* creates a duplicate design target
   * @callback
   */
  duplicateComponentDesignTarget = async () => {
    const openMetaComponent = this.getOpenMetaComponent();
    // copy object
    let toBeDuplicatedDesignTargetId = this.props.focusedDesignTargetId;
    let newDuplicateDesignTargetId = await this.props.duplicateDesignTarget(
      toBeDuplicatedDesignTargetId
    );
    this.props.createSelectedDesignTarget(
      newDuplicateDesignTargetId,
      openMetaComponent.id
    );
  };

  /**
   * it overrides the dimensions with a compatible one
   * @param {Object} designTarget - the given design target
   * @returns {Object} the design target with adapted dimensions
   */
  adaptDesignTargetDimensions(designTarget) {
    const openMetaComponent = this.getOpenMetaComponent();
    return {
      ...designTarget,
      NFWidth: openMetaComponent.width,
      NFHeight: openMetaComponent.height,
      unit: openMetaComponent.unit
    };
  }

  /**
   * it keeps the set point if it is compatible otherwise replaces with a compatible one
   * @param {Object} designTarget - the given design target
   * @returns {Object} the design target with adapted set point
   */
  adaptDesignTargetSetPoint(designTarget) {
    const { compatibleSetPointsIds } = this.props,
      compatibleSetPoint =
        designTarget.set_point &&
        compatibleSetPointsIds.includes(designTarget.set_point)
          ? designTarget.set_point
          : compatibleSetPointsIds[0];
    return {
      ...designTarget,
      set_point: compatibleSetPoint
    };
  }

  /**
   * it adapts the dimensions and the set point to compatible ones before creating the design target
   * @param {Object} designTarget - the given design target
   * @returns {Promise} the creation promise
   */
  adaptAndCreateDesignTarget(designTarget) {
    let adaptedDesignTarget = this.adaptDesignTargetDimensions(designTarget);
    adaptedDesignTarget = this.adaptDesignTargetSetPoint(adaptedDesignTarget);
    return this.props.createDesignTarget(adaptedDesignTarget);
  }

  /**
   * it removes and shows the warning message.
   * it is necessary otherwise the message is only shown once
   */
  showNoFamilyMemberWarning() {
    this.setState({ warnUserAboutFamilyMembers: false }, () =>
      this.setState({ warnUserAboutFamilyMembers: true })
    );
  }

  /**
   * it tells the state that the impor dialog is open.
   */
  openImportDialog = () => {
    this.setState({ importDialogOpen: true, importDialogMessage: "" });
  };

  handleClose = () => {
    this.setState({
      importDialogOpen: false,
      importProcessing: false,
      importDialogMessage: ""
    });
  };

  /**
   * it calls the on change callback with the dropped file and closes the import dialog
   * @param { File[] } acceptedFiles - an array containing the dropped file
   */
  handleFileDrop = async acceptedFiles => {
    const { onResetFileError } = this.props;
    if (acceptedFiles.length > 0) {
      if (onResetFileError) onResetFileError();
      this.setState({ importProcessing: true });
      const psrtFile = acceptedFiles[0];
      this.submitPsrt(psrtFile).catch(err => {
        this.setState({ importDialogMessage: err });
      });
    }
  };

  getUploadPsrtJobStatusWithDelay = debounce(() => {
    this.getUploadPsrtJobStatus();
  }, 10000);

  submitPsrt = psrtFile => {
    const { openMetaComponentId } = this.props;
    const formData = new FormData();
    formData.append("file", psrtFile);
    formData.append("meta_component_id", openMetaComponentId);
    return DesignTargetApi.startUploadPsrtJob(openMetaComponentId, formData)
      .then(() => this.getUploadPsrtJobStatusWithDelay())
      .catch(err => {
        this.setState({
          importProcessing: false,
          importDialogMessage: err?.response?.data
            ? err.response.data.message || err.response.data
            : "File upload error."
        });
      });
  };

  fetchData = () => {
    const {
      fetchDesignTargetsAction,
      fetchMCGroupsAndMetaComponentsAction,
      fetchScript2DsAction,
      fetchSelectedDesignTargetsAction,
      openMetaComponentId
    } = this.props;
    const requests = [];
    requests.push(
      fetchDesignTargetsAction(),
      fetchSelectedDesignTargetsAction(openMetaComponentId),
      fetchMCGroupsAndMetaComponentsAction(),
      fetchScript2DsAction()
    );
    return Promise.all(requests);
  };

  getUploadPsrtJobStatus() {
    const { openMetaComponentId } = this.props;
    return DesignTargetApi.getUploadPsrtJobStatus(openMetaComponentId)
      .then(resp => resp.data)
      .then(data => {
        const status = data.status;
        if (status === "QUEUED" || status === "RUNNING") {
          return this.getUploadPsrtJobStatusWithDelay();
        } else if (status === "DONE") {
          this.fetchData().then(() => {
            const message = data.errors?.warnings
              ? data.errors.warnings
              : "File upload completed.";
            this.setState({
              importDialogOpen: false,
              importProcessing: false,
              importDialogMessage: message
            });
          });
        } else if (status === "ERROR" || status === "FAILED") {
          this.setState({
            importProcessing: false,
            importDialogMessage:
              data.errors.errors ||
              data.errors.warnings ||
              "File upload failed."
          });
        }
      })
      .catch(err => {
        this.setState({
          importProcessing: false,
          importDialogMessage: err?.response?.data
            ? err.response.data.message || err.response.data
            : "File upload error."
        });
      });
  }

  render() {
    const {
        classes,
        designTargets,
        focusedDesignTargetId,
        focusOnDesignTarget,
        selectedDesignTargets
      } = this.props,
      {
        editingSelectedTargets,
        importDialogMessage,
        importProcessing,
        warnUserAboutFamilyMembers,
        designTargetUpdateProgress,
        snackbar
      } = this.state,
      isEditing = editingSelectedTargets !== null;
    const openMetaComponent = this.getOpenMetaComponent();
    return (
      <>
        <Import
          open={this.state.importDialogOpen}
          onClose={() => {
            this.setState({
              importDialogOpen: false,
              importDialogMessage: ""
            });
          }}
          onDrop={this.handleFileDrop}
          error={this.state.importDialogMessage}
          loading={this.state.importProcessing}
          message="Drag and drop your PSRT file here, or click to select file."
          accept={".psrt, .zip"}
          noTimeout={importProcessing ? true : false}
        />
        {designTargetUpdateProgress && (
          <JobStatusAndProgress
            jobType={"Design Targets update"}
            jobStatus={"RUNNING"}
            showStatus={true}
            showBigSpinner={true}
            progress={this.state.designTargetUpdateProgress}
            progressMessage={"Please do not leave this page."}
            stop={false}
            showStartButton={false}
            polling={false}
            starting={false}
          />
        )}
        {!designTargetUpdateProgress && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div test-data="titleWrapper">
                <Typography
                  className={classes.title}
                  variant="h5"
                  component="h3"
                >
                  Design Targets
                </Typography>
                <div className={classes.tooltip}>
                  <IconTooltip
                    text={"Selected design targets to build this component"}
                  />
                </div>
              </div>
              <div style={{ float: "right" }}>
                {!isEditing && focusedDesignTargetId != 0 && (
                  <IconButton
                    test-data="duplicateDesignTargetBtn"
                    aria-label="duplicate"
                    onClick={this.duplicateComponentDesignTarget}
                  >
                    <FileCopy />
                  </IconButton>
                )}
                {!isEditing && (
                  <IconButton
                    test-data="addDesignTargetBtn"
                    aria-label="add"
                    onClick={this.createComponentDesignTarget}
                  >
                    <AddBox />
                  </IconButton>
                )}
                {!isEditing && (
                  <Tooltip title="Import PSRT" placement="top">
                    <IconButton
                      test-data="importPSRTButton"
                      onClick={this.openImportDialog}
                    >
                      <CloudUpload />
                    </IconButton>
                  </Tooltip>
                )}
                {!isEditing && focusedDesignTargetId != 0 && (
                  <IconButton
                    test-data="deleteDesignTargetBtn"
                    aria-label="delete"
                    onClick={this.deleteComponentDesignTarget}
                  >
                    <Delete />
                  </IconButton>
                )}
                {!isEditing && selectedDesignTargets?.allIds.length > 0 && (
                  <Tooltip title="Recreate targets" placement="top">
                    <IconButton
                      test-data="recreateDesignTargetBtn"
                      aria-label="recreate"
                      onClick={this.recreateComponentDesignTargets}
                    >
                      <Sync />
                    </IconButton>
                  </Tooltip>
                )}
                <EditActions
                  isEditing={isEditing}
                  isSaving={false}
                  onEdit={this.editSelectedTargets}
                  onCancel={this.cancelEditingSelectedTargets}
                  onSave={this.save}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              {isEditing ? (
                <TargetsSelection
                  editingSelectedTargets={editingSelectedTargets}
                  designTargets={designTargets}
                  onSelectTarget={this.updateSelectedTargets}
                />
              ) : (
                <TargetsView
                  targetsList={this.getComponentTargets(
                    openMetaComponent.selected_design_targets
                  ).filter(({ id }) => id !== undefined)}
                  focusedDesignTargetId={focusedDesignTargetId}
                  focusOnDesignTarget={focusOnDesignTarget}
                />
              )}
            </Grid>
          </Grid>
        )}
        {importProcessing && (
          <DirectionSnackbar message="The upload is running in the background. You may click away and check the status on the job overview page." />
        )}
        {importDialogMessage && (
          <DirectionSnackbar message={importDialogMessage} />
        )}
        {warnUserAboutFamilyMembers && (
          <DirectionSnackbar message="The meta component must have selected family members." />
        )}
        {snackbar.visible && <DirectionSnackbar message={snackbar.message} />}
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    openMetaComponentId: DirectoryExplorerSelector.getMetaComponentOpenId(
      state
    ),
    metaComponents: DirectoryExplorerSelector.getMetaComponents(state),
    designTargets: DesignTargetSelector.getDesignTargets(state),
    selectedDesignTargets: SelectedDesignTargetSelector.getSelectedDesignTargets(
      state
    ),
    ffwfTargets: FFWTTargetSelector.getFFWTTargets(state),
    script2Ds: Script2DSelector.getScript2Ds(state),
    families: FamilySelector.getFamilies(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateMetaComponent: (id, newProperties) =>
      dispatch(DirectoryExplorerApi.updateMetaComponent(id, newProperties)),
    createDesignTarget: designTarget =>
      dispatch(DesignTargetApi.createDesignTarget(designTarget)),
    duplicateDesignTarget: designTargetId =>
      dispatch(DesignTargetApi.duplicateDesignTarget(designTargetId)),
    deleteDesignTargetAction: id =>
      dispatch(DesignTargetApi.deleteDesignTarget(id)),
    fetchDesignTargetsAction: () =>
      dispatch(DesignTargetApi.fetchDesignTargets()),
    updateDesignTargetAction: (id, properties) =>
      dispatch(DesignTargetApi.updateDesignTarget(id, properties)),
    showConfirmDialog: (
      title,
      message,
      confirmAction,
      cancelAction,
      isReduxAction,
      postConfirm,
      inputPlaceholders,
      uploadFile,
      confirmLabels
    ) =>
      dispatch(
        ConfirmDialogAction.show(
          title,
          message,
          confirmAction,
          cancelAction,
          isReduxAction,
          postConfirm,
          inputPlaceholders,
          uploadFile,
          confirmLabels
        )
      ),
    createSelectedDesignTarget: (designTargetId, metaComponentId) =>
      dispatch(
        SelectedDesignTargetApi.createSelectedDesignTarget(
          designTargetId,
          metaComponentId
        )
      ),
    createAndDeleteSelectedDesignTargets: (
      designTargetIds,
      metaComponentId,
      selectedDesignTargetIds
    ) =>
      dispatch(
        SelectedDesignTargetApi.createAndDeleteSelectedDesignTargets(
          designTargetIds,
          metaComponentId,
          selectedDesignTargetIds
        )
      ),
    fetchSelectedDesignTargetsAction: metaComponentId =>
      dispatch(
        SelectedDesignTargetApi.fetchSelectedDesignTargets(metaComponentId)
      ),
    fetchMCGroupsAndMetaComponentsAction: () =>
      dispatch(DirectoryExplorerApi.fetch()),
    fetchScript2DsAction: () => dispatch(Script2DApi.fetchScript2Ds())
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorBoundary(withStyles(styles)(ComponentTargets)));
